import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { workOrderApi, settlementApi, myVendorApi } from '../api';
import {
  INVENTORY_CATEGORIES, INVENTORY_STATUS_COLORS, ITEM_CATEGORIES,
  CUSTOM_PIPELINE, RENTAL_PIPELINE,
} from '../constants';

const PAGE_SIZE = 20;

export default function DressWorkOrders() {
  const navigate = useNavigate();

  // 월 네비게이터
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  const prevMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setPage(1);
  };
  const nextMonth = () => {
    const [y, m] = currentMonth.split('-').map(Number);
    const d = new Date(y, m, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    setPage(1);
  };

  const monthLabel = (() => {
    const [y, m] = currentMonth.split('-').map(Number);
    return `${y}년 ${m}월`;
  })();

  const monthDateFrom = `${currentMonth}-01`;
  const monthDateTo = (() => {
    const [y, m] = currentMonth.split('-').map(Number);
    const lastDay = new Date(y, m, 0).getDate();
    return `${currentMonth}-${String(lastDay).padStart(2, '0')}`;
  })();

  // 필터
  const [filterCategory, setFilterCategory] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterVendor, setFilterVendor] = useState('');

  // 데이터
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  // 인보이스 발송 여부 (contractIdx → settlement 정보)
  const [settlementMap, setSettlementMap] = useState<Record<number, any>>({});

  // 인보이스 모달
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceGroup, setInvoiceGroup] = useState<any>(null);
  const [invoiceItemPrices, setInvoiceItemPrices] = useState<Record<number, string>>({});
  const [invoiceForm, setInvoiceForm] = useState({
    vendorName: '',
    commissionRate: '',
    includeVat: true,
    bankName: '',
    accountNumber: '',
    sendMethod: 'email' as 'email' | 'fax',
    recipientEmail: '',
    recipientFax: '',
    memo: '',
  });
  const [invoiceStep, setInvoiceStep] = useState<'form' | 'done'>('form');
  const [createdSettlementIdx, setCreatedSettlementIdx] = useState<number | null>(null);

  // 품목 합계 계산
  const invoiceItemsTotal = Object.values(invoiceItemPrices).reduce((sum, v) => sum + Number(v || 0), 0);
  const invoiceCommission = invoiceItemsTotal * Number(invoiceForm.commissionRate || 0) / 100;
  const invoiceSupply = invoiceItemsTotal - invoiceCommission;
  const invoiceVat = invoiceForm.includeVat ? Math.round(invoiceSupply * 0.1) : 0;
  const invoiceSettlement = invoiceSupply + invoiceVat;

  const openInvoiceModal = async (group: any) => {
    setInvoiceGroup(group);
    // 품목별 가격 초기화
    const prices: Record<number, string> = {};
    group.items.forEach((item: any) => {
      const price = item.dressContractItem?.unitPrice || 0;
      prices[item.dressWorkOrderIdx] = String(Number(price));
    });
    setInvoiceItemPrices(prices);

    // 내 업체 정보로 은행/계좌 자동 채우기
    let bankName = '';
    let accountNumber = '';
    try {
      const vendor = await myVendorApi.get();
      bankName = vendor.bankName || '';
      accountNumber = vendor.accountNumber || '';
    } catch {}

    setInvoiceForm({
      vendorName: group.vendorName || '',
      commissionRate: '',
      includeVat: true,
      bankName,
      accountNumber,
      sendMethod: 'email',
      recipientEmail: '',
      recipientFax: '',
      memo: '',
    });
    setInvoiceStep('form');
    setCreatedSettlementIdx(null);
    setShowInvoiceModal(true);
  };

  const handleCreateAndSendInvoice = async () => {
    if (!invoiceForm.vendorName) {
      alert('업체명을 입력해주세요.');
      return;
    }
    if (invoiceItemsTotal <= 0) {
      alert('품목 금액을 입력해주세요.');
      return;
    }
    try {
      const result = await settlementApi.create({
        dressContractIdx: invoiceGroup.contractIdx,
        partnerName: invoiceForm.vendorName,
        totalSalesAmount: invoiceItemsTotal,
        commissionRate: Number(invoiceForm.commissionRate || 0),
        bankName: invoiceForm.bankName || undefined,
        accountNumber: invoiceForm.accountNumber || undefined,
        memo: invoiceForm.memo || undefined,
      });
      const idx = result?.dressPartnerSettlementIdx || result?.data?.dressPartnerSettlementIdx;
      setCreatedSettlementIdx(idx);

      let emailSent = false;

      // 이메일 발송 (PDF 첨부)
      if (invoiceForm.sendMethod === 'email' && invoiceForm.recipientEmail && idx) {
        try {
          await settlementApi.sendInvoice(idx, {
            recipientEmail: invoiceForm.recipientEmail,
            message: invoiceForm.memo || undefined,
          });
          emailSent = true;
        } catch (emailErr: any) {
          alert(`인보이스는 발행되었으나 이메일 발송에 실패했습니다: ${emailErr.message}`);
        }
      }

      // 팩스 선택 시 (추후 연동)
      if (invoiceForm.sendMethod === 'fax' && invoiceForm.recipientFax) {
        alert('팩스 발송 기능은 준비 중입니다.');
      }

      // settlementMap 갱신
      if (idx && invoiceGroup.contractIdx) {
        setSettlementMap(prev => ({ ...prev, [invoiceGroup.contractIdx]: { dressPartnerSettlementIdx: idx } }));
      }

      setInvoiceStep('done');
      if (emailSent) {
        alert('인보이스 발행 및 이메일 발송이 완료되었습니다.');
      }
    } catch (err: any) {
      alert(`인보이스 발행 실패: ${err.message}`);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const [data, settlements] = await Promise.all([
        workOrderApi.list({
          itemCategory: filterCategory || undefined,
          customerName: filterCustomer || undefined,
          partnerVendorName: filterVendor || undefined,
          dateFrom: monthDateFrom,
          dateTo: monthDateTo,
          page,
          size: PAGE_SIZE,
        }),
        settlementApi.list({ dateFrom: monthDateFrom, dateTo: monthDateTo, size: 999 }).catch(() => ({ items: [] })),
      ]);
      setItems(data.items || []);
      setTotal(data.total || 0);
      // contractIdx 기준으로 정산 맵 생성
      const sMap: Record<number, any> = {};
      (settlements.items || settlements || []).forEach((s: any) => {
        if (s.dressContractIdx) sMap[s.dressContractIdx] = s;
      });
      setSettlementMap(sMap);
    } catch (err) {
      console.error('입출고 내역 조회 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterCustomer, filterVendor, monthDateFrom, monthDateTo, page]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleSearch = () => {
    setPage(1);
    fetchItems();
  };

  const handleReset = () => {
    setFilterCategory('');
    setFilterCustomer('');
    setFilterVendor('');
    const now = new Date();
    setCurrentMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
    setPage(1);
  };

  const getCategoryLabel = (value: string) =>
    ITEM_CATEGORIES.find((c) => c.value === value)?.label || value || '-';

  const getStageColor = (stage: string) =>
    INVENTORY_STATUS_COLORS[stage] || 'bg-gray-100 text-gray-700';

  const formatDate = (dt: string | null) => {
    if (!dt) return '-';
    return dt.slice(0, 10);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-8">
        {/* 헤더 + 월 네비게이터 통합 */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">입출고관리</h1>
            <p className="text-sm text-gray-500 mt-1">드레스 입출고 및 대여현황을 관리합니다</p>
          </div>
          <div className="flex items-center gap-2 bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-1.5">
            <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
              <i className="ri-arrow-left-s-line text-lg text-gray-600"></i>
            </button>
            <span className="text-sm font-bold text-gray-800 min-w-[100px] text-center">{monthLabel}</span>
            <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors">
              <i className="ri-arrow-right-s-line text-lg text-gray-600"></i>
            </button>
          </div>
        </div>

        {/* 필터 + 카운트 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-3 mb-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <select
              value={filterCategory}
              onChange={(e) => { setFilterCategory(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">품목 전체</option>
              {INVENTORY_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <div className="relative">
              <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="고객명"
                className="border border-gray-300 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-[130px]"
              />
            </div>
            <div className="relative">
              <i className="ri-building-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
              <input
                type="text"
                value={filterVendor}
                onChange={(e) => setFilterVendor(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                placeholder="업체명"
                className="border border-gray-300 rounded-lg pl-8 pr-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-[130px]"
              />
            </div>
            <button onClick={handleSearch} className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">검색</button>
            {(filterCategory || filterCustomer || filterVendor) && (
              <button onClick={handleReset} className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer">초기화</button>
            )}
          </div>
          <span className="text-sm text-gray-500 flex-shrink-0">총 <span className="font-semibold text-gray-900">{total}</span>건</span>
        </div>

        {loading ? (
          <div className="flex justify-center p-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div></div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-16 text-center text-gray-400">
            <i className="ri-inbox-line text-5xl block mb-3"></i>데이터가 없습니다
          </div>
        ) : (() => {
          // 계약 단위로 그룹핑
          const groups: { contractIdx: number; customerName: string; customerId?: number; vendorName: string; date: string; items: any[] }[] = [];
          const groupMap = new Map<number, number>(); // contractIdx -> groups index

          items.forEach((item: any) => {
            const contractIdx = item.dressContractIdx || item.dressContract?.dressContractIdx || 0;
            const existing = groupMap.get(contractIdx);
            if (existing !== undefined && contractIdx !== 0) {
              groups[existing].items.push(item);
            } else {
              const idx = groups.length;
              groupMap.set(contractIdx, idx);
              groups.push({
                contractIdx,
                customerName: item.customerName || item.dressContract?.dressCustomer?.name || '-',
                customerPhone: item.dressContract?.dressCustomer?.phone || '',
                customerId: item.dressContract?.dressCustomer?.dressCustomerIdx,
                vendorName: item.partnerVendorName || item.dressContract?.partnerVendorName || '',
                contractType: item.dressContract?.contractType || item.workType || '',
                date: formatDate(item.updatedDt || item.startDt || item.createdDt),
                items: [item],
              });
            }
          });

          return (
            <div className="space-y-4">
              {groups.map((group, gi) => {
                const pipeline = group.items[0]?.workType === 'RENTAL' ? RENTAL_PIPELINE : CUSTOM_PIPELINE;
                return (
                  <div key={gi} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all">
                    {/* 그룹 헤더 */}
                    <div className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50/50 transition-colors"
                      onClick={() => {
                        if (group.contractIdx) navigate(`/dress-shop/contract-detail?id=${group.contractIdx}`);
                        else if (group.customerId) navigate(`/dress-shop/customer-detail?id=${group.customerId}`);
                      }}>
                      <div className="flex items-center gap-4">
                        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-pink-100 to-pink-50 flex items-center justify-center text-base font-bold text-pink-600 flex-shrink-0">
                          {group.customerName.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-base font-bold text-gray-900">{group.customerName}</span>
                            {group.contractType && (() => {
                              const ct = group.contractType;
                              const label = { CUSTOM: '맞춤', RENTAL: '대여', PURCHASE: '구매' }[ct] || ct;
                              const color = { CUSTOM: 'bg-blue-100 text-blue-700', RENTAL: 'bg-orange-100 text-orange-700', PURCHASE: 'bg-blue-100 text-blue-700' }[ct] || 'bg-gray-100 text-gray-600';
                              return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${color}`}>{label}</span>;
                            })()}
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{group.items.length}개 품목</span>
                          </div>
                          <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-400">
                            {group.customerPhone && <span><i className="ri-phone-line mr-0.5"></i>{group.customerPhone}</span>}
                            {group.vendorName && <span><i className="ri-building-line mr-0.5"></i>{group.vendorName}</span>}
                            <span><i className="ri-calendar-line mr-0.5"></i>계약일 {group.date}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {settlementMap[group.contractIdx] ? (
                          <span className="px-3 py-1.5 text-xs font-medium bg-green-100 text-green-700 rounded-lg flex items-center gap-1">
                            <i className="ri-checkbox-circle-fill"></i> 인보이스 발행완료
                          </span>
                        ) : group.vendorName ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); openInvoiceModal(group); }}
                            className="px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors flex items-center gap-1 cursor-pointer"
                          >
                            <i className="ri-file-text-line"></i> 인보이스
                          </button>
                        ) : (
                          <span className="px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-400 rounded-lg flex items-center gap-1">
                            <i className="ri-file-text-line"></i> 인보이스
                          </span>
                        )}
                        <i className="ri-arrow-right-s-line text-gray-300 text-xl"></i>
                      </div>
                    </div>

                    {/* 품목 리스트 */}
                    <div className="border-t border-gray-100">
                      {group.items.map((item: any, ii: number) => {
                        const contractItem = item.dressContractItem;
                        const productName = contractItem?.productName || '-';
                        const itemCategory = contractItem?.itemCategory || '';
                        const currentStage = item.currentStage || '미배정';

                        return (
                          <div key={item.dressWorkOrderIdx} className={`px-6 py-3 hover:bg-blue-50/20 transition-colors ${ii > 0 ? 'border-t border-gray-50' : ''}`}>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-900">{productName}</span>
                                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{getCategoryLabel(itemCategory)}</span>
                                  </div>
                                </div>
                              </div>
                              <select
                                value={currentStage}
                                onChange={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await workOrderApi.changeStage(item.dressWorkOrderIdx, e.target.value);
                                    fetchItems();
                                  } catch (err: any) {
                                    alert(`상태 변경 실패: ${err.message}`);
                                  }
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className={`text-xs px-3 py-1.5 rounded-lg border-0 font-semibold cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 ${getStageColor(currentStage)}`}
                              >
                                {pipeline.map((stage: string) => (
                                  <option key={stage} value={stage}>{stage}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
        <div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-1 px-4 py-4 border-t border-gray-100">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                이전
              </button>
              {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 10) {
                  pageNum = i + 1;
                } else if (page <= 5) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 4) {
                  pageNum = totalPages - 9 + i;
                } else {
                  pageNum = page - 4 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1.5 text-sm rounded-lg border ${
                      page === pageNum
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 인보이스 발급 모달 */}
      {showInvoiceModal && invoiceGroup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <div>
                <h2 className="text-lg font-bold text-gray-800">인보이스 발급</h2>
                <p className="text-sm text-gray-500 mt-0.5">{invoiceGroup.customerName} - {invoiceGroup.items.length}개 품목</p>
              </div>
              <button onClick={() => setShowInvoiceModal(false)} className="text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* 업체명 */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">업체명 *</label>
                <input type="text" value={invoiceForm.vendorName}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, vendorName: e.target.value })}
                  placeholder="업체명"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              </div>

              {/* 품목별 금액 */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">품목 내역</p>
                <div className="space-y-2">
                  {invoiceGroup.items.map((item: any) => (
                    <div key={item.dressWorkOrderIdx} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700 truncate">{item.dressContractItem?.productName || '-'}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${getStageColor(item.currentStage)}`}>{item.currentStage}</span>
                        </div>
                      </div>
                      <input
                        type="number"
                        value={invoiceItemPrices[item.dressWorkOrderIdx] || ''}
                        onChange={(e) => setInvoiceItemPrices((prev) => ({ ...prev, [item.dressWorkOrderIdx]: e.target.value }))}
                        className="w-32 border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-right focus:ring-2 focus:ring-blue-500"
                        placeholder="0"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-3 pt-3 border-t border-gray-200">
                  <span className="text-sm font-semibold text-gray-700">합계</span>
                  <span className="text-sm font-bold text-gray-900">{invoiceItemsTotal.toLocaleString()}원</span>
                </div>
              </div>

              {/* 수수료 / 정산 */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">수수료율 (%)</label>
                  <input type="number" value={invoiceForm.commissionRate}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, commissionRate: e.target.value })}
                    placeholder="0"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
                </div>

                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input type="checkbox" checked={invoiceForm.includeVat}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, includeVat: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                  <span className="text-sm text-gray-700">부가세 (10%) 포함</span>
                </label>

                {invoiceItemsTotal > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4 space-y-2 text-sm">
                    {Number(invoiceForm.commissionRate || 0) > 0 && (
                      <>
                        <div className="flex justify-between"><span className="text-gray-600">수수료액</span><span className="font-medium">{invoiceCommission.toLocaleString()}원</span></div>
                        <div className="flex justify-between"><span className="text-gray-600">공급가</span><span className="font-medium">{invoiceSupply.toLocaleString()}원</span></div>
                      </>
                    )}
                    {invoiceForm.includeVat && (
                      <div className="flex justify-between"><span className="text-gray-600">부가세 (10%)</span><span className="font-medium">{invoiceVat.toLocaleString()}원</span></div>
                    )}
                    <div className="flex justify-between border-t border-blue-200 pt-2">
                      <span className="font-semibold text-blue-800">정산금액</span>
                      <span className="font-bold text-blue-800 text-base">{invoiceSettlement.toLocaleString()}원</span>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">은행명</label>
                    <input type="text" value={invoiceForm.bankName}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, bankName: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">계좌번호</label>
                    <input type="text" value={invoiceForm.accountNumber}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, accountNumber: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>
              </div>

              {/* 발송 정보 */}
              <div className="space-y-3 pt-2 border-t border-gray-100">
                <p className="text-sm font-semibold text-gray-700">발송 정보</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setInvoiceForm({ ...invoiceForm, sendMethod: 'email' })}
                    className={`flex-1 py-2 text-sm rounded-lg border font-medium cursor-pointer transition-colors ${
                      invoiceForm.sendMethod === 'email'
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}>
                    <i className="ri-mail-line mr-1"></i>이메일
                  </button>
                  <button
                    onClick={() => setInvoiceForm({ ...invoiceForm, sendMethod: 'fax' })}
                    className={`flex-1 py-2 text-sm rounded-lg border font-medium cursor-pointer transition-colors ${
                      invoiceForm.sendMethod === 'fax'
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}>
                    <i className="ri-printer-line mr-1"></i>팩스
                  </button>
                </div>
                {invoiceForm.sendMethod === 'email' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">수신 이메일</label>
                    <input type="email" value={invoiceForm.recipientEmail}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, recipientEmail: e.target.value })}
                      placeholder="partner@example.com"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
                  </div>
                )}
                {invoiceForm.sendMethod === 'fax' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">수신 팩스번호</label>
                    <input type="tel" value={invoiceForm.recipientFax}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, recipientFax: e.target.value })}
                      placeholder="02-1234-5678"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
                    <p className="mt-1 text-xs text-amber-500">팩스 발송 기능은 준비 중입니다</p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">메모</label>
                  <textarea value={invoiceForm.memo}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, memo: e.target.value })}
                    rows={2}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none" />
                </div>
              </div>
            </div>

            {/* 하단 버튼 */}
            <div className="p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl">
              {invoiceStep === 'done' && createdSettlementIdx ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-emerald-600 text-sm font-medium">
                    <i className="ri-checkbox-circle-fill text-lg"></i>
                    인보이스가 발행되었습니다
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const url = settlementApi.invoicePdfUrl(createdSettlementIdx);
                        const token = localStorage.getItem('accessToken');
                        window.open(`${url}?token=${token}`, '_blank');
                      }}
                      className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm cursor-pointer flex items-center justify-center gap-2">
                      <i className="ri-eye-line"></i> PDF 미리보기
                    </button>
                    <button
                      onClick={async () => {
                        const url = settlementApi.invoicePdfUrl(createdSettlementIdx);
                        const token = localStorage.getItem('accessToken');
                        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
                        const blob = await res.blob();
                        const a = document.createElement('a');
                        a.href = URL.createObjectURL(blob);
                        a.download = `invoice-${createdSettlementIdx}.pdf`;
                        a.click();
                        URL.revokeObjectURL(a.href);
                      }}
                      className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm cursor-pointer flex items-center justify-center gap-2">
                      <i className="ri-download-line"></i> PDF 다운로드
                    </button>
                  </div>
                  <button onClick={() => setShowInvoiceModal(false)}
                    className="w-full px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm cursor-pointer">
                    닫기
                  </button>
                </div>
              ) : (
                <div className="flex justify-end gap-3">
                  <button onClick={() => setShowInvoiceModal(false)}
                    className="px-5 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm cursor-pointer">
                    취소
                  </button>
                  <button onClick={handleCreateAndSendInvoice}
                    className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm cursor-pointer flex items-center gap-2">
                    {(invoiceForm.sendMethod === 'email' && invoiceForm.recipientEmail) ? (
                      <><i className="ri-send-plane-line"></i> 인보이스 발행 및 발송</>
                    ) : (
                      '인보이스 발행'
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
